/**
 * Apples to Apples on the laptop. The game never asks the host anything, so every
 * control here is core's (scores, participants, TV control) plus the deck editor.
 */

import { useCallback, useState } from 'react';
import { JoinQr, Scoreboard, TvLink, platform, type GameUiProps } from '@gsf/kit';
import { applesView } from './view';
import { DeckEditor } from './DeckEditor';

export function ApplesHost({ snapshot, connected, notify }: GameUiProps) {
  const view = applesView(snapshot);
  const { core, shared } = view;
  // Ownership, decided by core: edit the deck (pack) and run session admin (scores, players, TV).
  const canEdit = snapshot.you.canEditContent;
  const canManage = snapshot.you.canManageSession;
  const [busy, setBusy] = useState(false);

  /** Any of core's routes, with the toast wired to the failure. */
  const run = useCallback(
    async (what: string, call: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await call();
      } catch (err) {
        notify(err instanceof Error ? err.message : `Could not ${what}.`);
      } finally {
        setBusy(false);
      }
    },
    [notify],
  );

  return (
    <div className="host">
      <div className="host-bar">
        <span className="code-chip">{core.code}</span>
        <strong>{core.title}</strong>
        <span className="faint">
          {shared.phase === 'lobby'
            ? `${String(shared.readyBy.length)} of ${String(view.standings.length)} ready`
            : `round ${String(shared.round)} of ${String(shared.rounds)} · ${shared.phase}`}
        </span>
        <span className="spacer" />
        {!connected ? <span className="tag tag-bad">reconnecting…</span> : null}
        {/* The TV toggle is authority and the link is a credential: both the owner's, not a co-host's. */}
        {canManage ? (
          <>
            <label className="host-toggle" title="Let the TV drive — this game has nothing to drive">
              <input
                type="checkbox"
                checked={core.displayCanDrive}
                disabled={busy}
                onChange={(event) =>
                  void run('change that', () =>
                    platform.setDisplayCanDrive(core.code, event.target.checked),
                  )
                }
              />
              TV control
            </label>
            <TvLink code={core.code} />
          </>
        ) : null}
      </div>

      <div className="host-body">
        <div className="host-col">
          {/* No game controls: the room starts itself and the judge moves the round on. */}
          <div className="panel col">
            <h2 className="panel-title">The round</h2>
            <p className="faint" style={{ margin: 0 }}>
              {shared.phase === 'lobby'
                ? `Waiting for everyone to press "I'm ready". Needs ${String(shared.minPlayers)} players.`
                : shared.phase === 'over'
                  ? 'The segment is finished.'
                  : `${view.judgeName ?? 'Somebody'} is the judge.`}
            </p>
            {shared.green ? <p className="apples-green apples-green-host">{shared.green.text}</p> : null}
            {/* The two greens the judge is choosing between — the only thing on this
                screen the room may not see (`viewPrivate`, host). */}
            {view.host && view.host.candidates.length > 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                Choosing between: {view.host.candidates.map((card) => card.text).join(' / ')}
              </p>
            ) : null}
          </div>

          {canEdit ? (
            <DeckEditor gameId={core.segment?.gameId ?? 'apples-to-apples'} notify={notify} />
          ) : null}
        </div>

        <div className="host-col">
          <div className="panel col">
            <h2 className="panel-title">Green apples</h2>
            <Scoreboard
              players={view.standings}
              activeParticipantId={shared.judgeId}
              showPresence
              onAdjust={
                canManage
                  ? (participantId, delta) =>
                      void run('adjust that score', () =>
                        platform.adjustScore(core.code, participantId, delta),
                      )
                  : undefined
              }
              onRemove={
                canManage
                  ? (participantId) =>
                      void run('remove that player', () =>
                        platform.removePlayer(core.code, participantId),
                      )
                  : undefined
              }
              onMerge={
                canManage
                  ? (fromId, intoId) =>
                      void run('merge those players', () =>
                        platform.mergePlayers(core.code, fromId, intoId),
                      )
                  : undefined
              }
            />
          </div>

          <div className="panel col">
            <h2 className="panel-title">Join</h2>
            <JoinQr code={core.code} />
          </div>
        </div>
      </div>
    </div>
  );
}
